<template>
  <v-container id="event-roles--edits">
    <v-row class="mb-4">
      <v-col cols="8">
        <h2>
          {{
            $t(
              "common:common.staff.label",
              "Staff",
              "Label of the staff section"
            )
          }}
        </h2>
      </v-col>
      <v-col class="text-right">
        <v-btn
          v-if="canAdd"
          id="editRolesEvent"
          v-edit-vertical-line="{
            targetSelector: '#event-roles--edits'
          }"
          color="edit"
          data-test="eventStaffAdd"
          @click="addItem"
        >
          <v-icon color="white" size="small" start> $iconPlus </v-icon>
          {{ $t("common:button.add", "Add", "Add button") }}
        </v-btn>
      </v-col>
    </v-row>

    <GuidanceMessages
      location="EventProfilePage.EventStaffTab"
      entity-type="Event"
      :entity-id="event.id"
    />
    <div data-test="eventStaffList">
      <AppStaffList
        :staffs="event.personRoles"
        :can-add="canAdd"
        no-edit
        @delete="personRevokeRoleInEvent({ ...$event, eventId: event.id })"
      />
    </div>

    <EventStaffFunctions
      v-if="
        event.useEventStaffFunctions &&
        (event.eventStaffFunctionsConf?.visibility == 'public' ||
          staffFunctionsViewAllowed)
      "
      :event="event"
      data-test="eventStaffFunctionsList"
    />

    <EventPersonRoleDialog ref="form" :event="event" />
  </v-container>
</template>

<script>
import gql from "fraql";

import editVerticalLine from "@/directives/edit-vertical-line";
import AppStaffList from "@/components/elements/AppStaffList.vue";
import EventPersonRoleDialog from "./EventPersonRoleDialog.vue";
import GuidanceMessages from "@elements/GuidanceMessages.vue";
import { PersonRoleFragment } from "./personRoleMethods";
import EventStaffFunctions from "./EventStaffFunctions.vue";
import { showLoadingButton } from "@/utils/showLoadingButton";
import {
  injectAppConfirm,
  injectSnackbar,
  injectSnackbarError
} from "@/composables/injections";
import { useTranslation } from "i18next-vue";
import { apolloClient } from "@/plugins/apollo";

export function usePersonRevoleRoleInEventHelper() {
  const { appConfirm } = injectAppConfirm();
  const { snackbar } = injectSnackbar();
  const { snackbarServerError } = injectSnackbarError();
  const { t } = useTranslation();

  async function personRevokeRoleInEvent({
    evt,
    value: personRole,
    eventId,
    mutationOptions = {}
  }) {
    const response = await appConfirm(
      t(
        "events:EventPage.Schedule.OrganiserAndOfficials.deleteConfirm.heading",
        "Remove staff member from event"
      ),
      t(
        [
          "events:EventPage.Schedule.OrganiserAndOfficials.deleteConfirm.deleteConfirm.question",
          "Are you sure you want to remove staff / team member {{personName}} from event?"
        ],
        { personName: personRole.person?.name }
      )
    );
    if (!response) return;
    const removeLoading = showLoadingButton(evt.target);
    try {
      await apolloClient.mutate({
        mutation: gql`
          mutation personRevokeRoleInEvent($personRoleId: ID!, $eventId: ID!) {
            personRevokeRoleInEvent(
              input: { personRoleId: $personRoleId, eventId: $eventId }
            ) {
              event {
                id
                personRoles {
                  id
                }
              }
            }
          }
        `,
        ...mutationOptions,
        variables: { personRoleId: personRole.id, eventId }
      });
      snackbar("success");
    } catch (error) {
      snackbarServerError("error", { text: error });
    } finally {
      removeLoading();
    }
  }

  return {
    personRevokeRoleInEvent
  };
}

export default {
  name: "EventRolesManagement",
  components: {
    AppStaffList,
    EventPersonRoleDialog,
    GuidanceMessages,
    EventStaffFunctions
  },
  directives: { editVerticalLine },

  props: {
    event: {
      type: Object,
      required: true
    }
  },
  setup() {
    const { personRevokeRoleInEvent } = usePersonRevoleRoleInEventHelper();
    return {
      personRevokeRoleInEvent
    };
  },
  computed: {
    canAdd() {
      return this.$allowedMutation(
        this.event.allowedMutations,
        "personGrantRoleInEvent"
      );
    },
    canDelete() {
      return this.$allowedMutation(
        this.event.allowedMutations,
        "personRevokeRoleInEvent"
      );
    },
    staffFunctionsViewAllowed() {
      return (
        this.$allowedMutation(this.event.allowedMutations, [
          "eventStaffFunctionsConfCreate"
        ]) ||
        this.$allowedMutation(
          this.event.eventStaffFunctionsConf?.allowedMutations,
          "eventStaffFunctionsConfUpdate"
        )
      );
    }
  },
  fragments: {
    eventFields: gql`
      fragment _ on Event {
        useEventStaffFunctions
      }
    `,
    disciplineFields: gql`
      fragment _ on Discipline {
        ${EventPersonRoleDialog.fragments.disciplineFields}
      }
    `,
    personRoles: gql`
      fragment _ on Event {
        startDate
        personRoles {
          ${PersonRoleFragment}
        }
      }
    `,
    personRoleFields: PersonRoleFragment
  },
  methods: {
    addItem() {
      this.$refs.form.openForm({});
    }
  }
};
</script>
